import { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Typography, useTheme, Zoom } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { MAX_LEVEL_DESCRIPTION_LENGTH } from 'src/feature/dashboard/constants/skill-assessment-constants'
import { useEditMode } from 'src/feature/dashboard/hook/useEditMode'
import { useSkillValidation } from 'src/feature/dashboard/hook/useSkillValidation'
import { saveSkillUpdates } from 'src/feature/dashboard/util/skill-update-utils'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { addExpectedLevel } from 'src/feature/skill-assessment/api/skill-assessment-api'
import { useSkillAssessmentStore } from 'src/feature/skill-assessment/store/skill-assessment.store'
import { getCardRatingColor } from 'src/feature/my-team/util/skillGapUtils'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { EditModeButtons } from '../molecule/EditModeButtons'
import { LevelItem } from '../molecule/LevelItem'
import { SkillHeader } from '../organism/SkillHeader'
import { SkillSet } from 'src/feature/skill-assessment/model/skill-assessment-model'

const RATING_BOX_VALUES = [1, 2, 3, 4, 5] as const

export type ExpectedLevelsProps = {
  isModalClose: () => void
  isModalOpen: boolean
  skillAssessmentSetIndex: number
}

/**
 * Modal component for setting expected skill levels and editing level descriptions
 * Features:
 * - Step-by-step navigation through skills
 * - Bulk edit mode for all level descriptions
 * - Single API call for saving all changes
 */
export const ExpectedLevels = observer((props: ExpectedLevelsProps) => {
  const { translation } = useLocalizationStore()
  const skillAssessmentStore = useSkillAssessmentStore()
  const notificationStore = useNotificationStore()
  const theme = useTheme()

  const translations = translation.pages.myTeam.rolesButton
  const skillAssessmentSet = skillAssessmentStore.skillCategory ?? {
    id: '',
    category_name: '',
    category_description: '',
    skills: [],
  }
  const [activeStep, setActiveStep] = useState<number>(props.skillAssessmentSetIndex)
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null)
  
  const [editModeState, editModeActions] = useEditMode()
  const { isEditMode, editedDescriptions, editedSkillName, editedSkillHint } = editModeState
  const validateSkillData = useSkillValidation()

  const currentSkill = skillAssessmentSet?.skills[activeStep]
  const getSortedLevels = (skill?: SkillSet) => [...(skill?.levels ?? [])].sort((a, b) => a.setLevel - b.setLevel)
  const currentSkillLevels = useMemo(() => getSortedLevels(currentSkill), [currentSkill])

  useEffect(() => {
    setActiveStep(props.skillAssessmentSetIndex)
  }, [props.skillAssessmentSetIndex])

  useEffect(() => {
    if (!props.isModalOpen) {
      setEditingSkillIndex(null)
      editModeActions.resetEditMode()
    }
  }, [editModeActions, props.isModalOpen])

  const handleLevelClick = async (skill: SkillSet, expectedSetLevel: number) => {
    const { id: setId, setName } = skill
    await addExpectedLevel(setId, setName, expectedSetLevel)
    skill.expected_set_level = expectedSetLevel
  }

  const handleStartEditMode = (skillIndex: number) => {
    const skill = skillAssessmentSet.skills[skillIndex]

    if (!skill) {
      return
    }

    setActiveStep(skillIndex)
    setEditingSkillIndex(skillIndex)
    editModeActions.toggleEditMode(getSortedLevels(skill), skill.setName || '', skill.setHint || '')
  }

  const handleSelectSkill = (skillIndex: number) => {
    if (editingSkillIndex !== null && editingSkillIndex !== skillIndex) {
      handleCancelEditMode()
    }

    setActiveStep(skillIndex)
  }

  const handleCancelEditMode = () => {
    setEditingSkillIndex(null)
    editModeActions.resetEditMode()
  }

  const handleSaveAllEdits = async () => {
    if (!validateSkillData(editedSkillName, editedSkillHint, editedDescriptions)) {
      return
    }

    try {
      await saveSkillUpdates(currentSkill, editedSkillName, editedSkillHint, editedDescriptions, currentSkillLevels)
      setEditingSkillIndex(null)
      editModeActions.resetEditMode()
      notificationStore.enqueueSuccess(translation.pages.myTeam.errorMsg.changesSavedSuccessfully())
    } catch {
      notificationStore.enqueueError(translation.util.unknownErrorOccurred())
    }
  }

  const handleDescriptionChange = (levelId: string, value: string) => {
    editModeActions.updateDescription(levelId, value, MAX_LEVEL_DESCRIPTION_LENGTH)
  }

  const getRatingBoxSx = (rating: number, selectedRating: number) => {
    if (!selectedRating) {
      return {
        ...styles.ratingBox,
        borderColor: ColorTokens.border100,
        backgroundColor: 'background.dark',
        color: 'text.primary',
      }
    }

    const boxColor = getCardRatingColor(selectedRating)
    const isFilled = selectedRating >= rating

    return {
      ...styles.ratingBox,
      borderColor: boxColor,
      backgroundColor: isFilled ? boxColor : 'common.white',
      color: isFilled ? 'common.white' : boxColor,
    }
  }

  return (
    <ModalWindow
      modalProps={{
        open: props.isModalOpen,
        onClose: () => props.isModalClose(),
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '80%', lg: '50%' },
            borderRadius: '12px',
          },
        },
      }}
      titleProps={{
        textValue: skillAssessmentStore.skillCategory?.category_name || '',
      }}
      subtitleProps={{
        textValue: skillAssessmentStore.skillCategory?.category_description || '',
        sx: { color: 'text.secondary' },
      }}
    >
      <Zoom in={props.isModalOpen} timeout={theme.transitions.duration.standard} mountOnEnter unmountOnExit>
        <AppBox sx={styles.mainContainer}>
          <AppBox sx={styles.skillList}>
            {skillAssessmentSet.skills.map((skill, skillIndex) => {
              const isExpanded = activeStep === skillIndex
              const isEditingRow = isEditMode && editingSkillIndex === skillIndex
              const skillLevels = getSortedLevels(skill)

              return (
                <AppPaper
                  key={skill.id}
                  onClick={() => handleSelectSkill(skillIndex)}
                  sx={{
                    ...styles.skillCard,
                    backgroundColor: isExpanded ? ColorTokens.primary10 : 'background.paper',
                  }}
                >
                  <AppBox sx={styles.skillSummaryRow}>
                    <AppBox sx={styles.skillTextColumn}>
                      {isEditingRow ? (
                        <SkillHeader
                          isEditing
                          skillName={editedSkillName || skill.setName || ''}
                          skillHint={editedSkillHint || skill.setHint || ''}
                          onSkillNameChange={editModeActions.updateSkillName}
                          onSkillHintChange={editModeActions.updateSkillHint}
                        />
                      ) : (
                        <>
                          <Typography variant='h5'>{skill.setName}</Typography>
                          <Typography variant='body2' sx={{ color: 'text.secondary' }}>{skill.setHint}</Typography>
                        </>
                      )}
                    </AppBox>

                    <AppBox sx={styles.ratingBoxes}>
                      {(() => {
                        const selectedRating = skill.expected_set_level ?? 0

                        return RATING_BOX_VALUES.map(rating => (
                          <AppBox
                            key={`${skill.id}-${rating}`}
                            component='button'
                            type='button'
                            sx={getRatingBoxSx(rating, selectedRating)}
                            onClick={() => handleLevelClick(skill, rating)}
                            aria-label={`${skill.setName} level ${rating}`}
                          >
                            {rating}
                          </AppBox>
                        ))
                      })()}
                    </AppBox>
                  </AppBox>

                  {isExpanded && (
                    <AppBox sx={styles.expandedLevels}>
                      {skillLevels.map((level, index) => (
                        <LevelItem
                          key={level.id}
                          level={level}
                          index={index}
                          isSelected={level.setLevel === skill.expected_set_level}
                          isEditing={isEditingRow}
                          editedDescription={editedDescriptions[level.id] ?? level.setLevelDescription}
                          onLevelClick={() => !isEditMode && currentSkill && handleLevelClick(currentSkill, level.setLevel)}
                          onDescriptionChange={e => handleDescriptionChange(level.id, e.target.value)}
                        />
                      ))}

                      <AppBox sx={styles.inlineEditRow}>
                        <EditModeButtons
                          isEditMode={isEditingRow}
                          onEdit={() => handleStartEditMode(skillIndex)}
                          onCancel={handleCancelEditMode}
                          onSave={handleSaveAllEdits}
                          editButtonLabel={translations.edit()}
                          cancelButtonLabel={translations.cancel()}
                          saveAllButtonLabel={translations.saveAll()}
                        />
                      </AppBox>
                    </AppBox>
                  )}
                </AppPaper>
              )
            })}
          </AppBox>
        </AppBox>
      </Zoom>
    </ModalWindow>
  )
})

const styles = {
  mainContainer: {
    marginTop: '0.5rem',
    flexDirection: 'column',
    gap: 10,
    width: { xs: '100%' },
  },
  skillList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  skillCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '10px 15px',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  skillSummaryRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  skillTextColumn: {
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  ratingBoxes: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    flexWrap: 'nowrap',
  },
  ratingBox: {
    width: 30,
    height: 30,
    borderRadius: '6px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    padding: 0,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  expandedLevels: {
    flexDirection: 'column',
    gap: 5,
    width: '100%',
  },
  inlineEditRow: {
    width: '100%',
    justifyContent: 'flex-end',
    gap: 5,
    alignItems: 'center',
  },
}
